import { message } from "antd";
import { useTranslation } from "react-i18next";

import { productsActions } from "@fitness-app/app-store";
import { type CustomInvoicesClient, type InvoicesClient } from "@fitness-app/data-models/entities/Product";

import { LIST_ITEM_SEPARATOR } from "~/modules/Products/constants/listSeparator";
import { useAppDispatch } from "~/store/initializeStore";

function gatewayIdFromSelectValue(value: string): string | null {
  const parts = value.split(LIST_ITEM_SEPARATOR);
  const id = parts[0];
  if (parts.length === 1) {
    return id || null;
  }
  return parts[1] === "false" ? id || null : null;
}

export interface DuplicateProductFormModel {
  sourceProductId: string;
  name: string;
  description: string;
  images: { url: string }[] | string[] | null;
  customStripeAccount?: string | null;
  tpayIntegration?: string | null;
  customInvoicesClient?: string | null;
}

export const useDuplicateProduct = (successCallback?: () => void) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(["products"]);

  const handleSubmit = (data: DuplicateProductFormModel) => {
    let tpayIntegration: string | null | undefined = undefined;
    let customStripeAccount: string | null | undefined = undefined;
    let customInvoicesClient: CustomInvoicesClient | null | undefined = undefined;

    if (data.tpayIntegration === null) {
      tpayIntegration = null;
    } else if (data.tpayIntegration) {
      tpayIntegration = gatewayIdFromSelectValue(data.tpayIntegration);
    }

    if (data.customStripeAccount === null) {
      customStripeAccount = null;
    } else if (data.customStripeAccount) {
      customStripeAccount = gatewayIdFromSelectValue(data.customStripeAccount);
    }

    if (data.customInvoicesClient === null) {
      customInvoicesClient = null;
    } else if (data.customInvoicesClient) {
      const [accountName, client, accountUrl] = data.customInvoicesClient.split(LIST_ITEM_SEPARATOR);
      customInvoicesClient = {
        accountName: accountName ?? "",
        accountUrl: accountUrl ?? "",
        client: client as InvoicesClient,
      };
    }

    const onSuccess = () => {
      void message.success(t("messages.success.duplicateProduct"));
      successCallback?.();
    };

    const onFailure = () => {
      void message.error(t("messages.error.duplicateProduct"));
    };

    void dispatch(
      productsActions.duplicateProduct({
        payload: {
          sourceProductId: data.sourceProductId,
          name: data.name,
          description: data.description,
          images: data.images?.map((image) => (typeof image === "string" ? image : image.url)) ?? [],
          tpayIntegration,
          customStripeAccount,
          customInvoicesClient,
        },
        onSuccess,
        onFailure,
      }),
    );
  };

  return { handleSubmit };
};
